import ListPagination from '../components/common/ListPagination';
import useListPagination from '../hooks/useListPagination';
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CalendarIcon,
  ClockIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import { useAuth } from '../contexts/AuthContext';
import { useHeaderSearch } from '../contexts/SearchContext';
import LeaveService from '../services/leave.service';
import WorkScheduleService from '../services/workSchedule.service';
import toast from '../utils/toast';
import { getUpcomingHolidays } from '../utils/holidayUtils';
import { formatDateDisplay } from '../utils/dateDisplay';

const toDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const LeaveRequest = () => {
  const { user } = useAuth();
  const { headerSearch } = useHeaderSearch();
  const [showForm, setShowForm] = useState(false);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [cancellationRequests, setCancellationRequests] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState({});
  const [loading, setLoading] = useState(true);
  const [upcomingHolidays, setUpcomingHolidays] = useState([]);
  const [scheduledDays, setScheduledDays] = useState(null);
  const [scheduleError, setScheduleError] = useState('');
  const [cancellingLeave, setCancellingLeave] = useState(null);
  const [cancellationReason, setCancellationReason] = useState('');
  const [submittingCancellation, setSubmittingCancellation] = useState(false);
  const [formData, setFormData] = useState({
    leaveType: '',
    startDate: '',
    endDate: '',
    reason: '',
    isHalfDay: false,
    halfDayPeriod: 'morning'
  });
  const earliestLeaveDate = new Date();
  earliestLeaveDate.setHours(0, 0, 0, 0);
  earliestLeaveDate.setDate(earliestLeaveDate.getDate() - 15);
  const minimumLeaveDate = toDateInputValue(earliestLeaveDate);

  useEffect(() => {
    let active = true;
    setScheduledDays(null); setScheduleError('');
    if (!formData.startDate || (!formData.isHalfDay && !formData.endDate)) return undefined;
    WorkScheduleService.leaveDays({ fromDate: formData.startDate, toDate: formData.isHalfDay ? formData.startDate : formData.endDate, isFullDay: !formData.isHalfDay })
      .then(result => { if (active) setScheduledDays(result.days); })
      .catch(error => { if (active) setScheduleError(error.response?.data?.message || 'Unable to calculate working leave days'); });
    return () => { active = false; };
  }, [formData.startDate, formData.endDate, formData.isHalfDay]);

  useEffect(() => {
    fetchLeaveData();
    fetchUpcomingHolidays();
  }, []);

  const fetchUpcomingHolidays = async () => {
    try {
      const startDate = new Date();
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + 30);

      const holidays = await getUpcomingHolidays(startDate, endDate, 'IN');
      setUpcomingHolidays(holidays);
    } catch (error) {
      console.error('Error fetching upcoming holidays:', error);
      setUpcomingHolidays([]);
    }
  };

  const fetchLeaveData = async () => {
    try {
      setLoading(true);

      const [requestsRes, balanceRes, typesRes, cancellationsRes] = await Promise.all([
        LeaveService.getMyRequests().catch(() => []),
        LeaveService.getLeaveBalance().catch(() => ({})),
        LeaveService.getLeaveTypes().catch(() => []),
        LeaveService.getAllCancelRequests().catch(() => ({ requests: [] })),
      ]);

      setLeaveRequests(requestsRes || []);
      setLeaveBalance(balanceRes || {});
      setLeaveTypes(typesRes || []);
      setCancellationRequests(cancellationsRes?.requests || []);
    } catch (error) {
      console.error('Error fetching leave data:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to load leave data';
      toast.error(errorMessage);
      setLeaveRequests([]);
      setLeaveBalance({});
      setLeaveTypes([]);
      setCancellationRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.startDate || !formData.reason) { // Remove leaveType validation
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);

      await LeaveService.createLeaveRequest({
        fromDate: formData.startDate,
        toDate: formData.endDate,
        reason: formData.reason,
        isFullDay: !formData.isHalfDay,
        halfDayPeriod: formData.isHalfDay ? formData.halfDayPeriod : null, // Send period for half-day
        employeeId: user.empcode,
        status: 'pending'
      });

      toast.success('Leave request submitted successfully!');
      setShowForm(false);
      setFormData({
        leaveType: '',
        startDate: '',
        endDate: '',
        reason: '',
        isHalfDay: false,
        halfDayPeriod: 'morning'
      });

      await fetchLeaveData();
    } catch (error) {
      console.error('Error submitting leave request:', error);
      toast.error(error.message || error.response?.data?.message || 'Failed to submit leave request');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const submitCancellation = async () => {
    if (!cancellingLeave || !cancellationReason.trim()) {
      toast.error('Please provide a reason for cancelling this leave.');
      return;
    }

    try {
      setSubmittingCancellation(true);
      await LeaveService.createCancelRequest({
        leaveRequestId: cancellingLeave.id,
        reason: cancellationReason.trim(),
      });
      toast.success('Cancellation request sent to HR for approval.');
      setCancellingLeave(null);
      setCancellationReason('');
      await fetchLeaveData();
    } catch (error) {
      toast.error(error.message || 'Failed to submit cancellation request');
    } finally {
      setSubmittingCancellation(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (String(status || '').toLowerCase()) {
      case 'approved': return <CheckCircleIcon className="w-5 h-5 text-green-600" />;
      case 'rejected': return <XCircleIcon className="w-5 h-5 text-red-600" />;
      case 'pending': return <ClockIcon className="w-5 h-5 text-yellow-600" />;
      default: return <ExclamationTriangleIcon className="w-5 h-5 text-gray-600" />;
    }
  };

  const getCancellationStatus = (leaveId) => cancellationRequests.find(
    (request) => String(request.id) === String(leaveId)
  )?.status?.toLowerCase();

  const filteredLeaveRequests = headerSearch
    ? leaveRequests.filter(r => {
      const q = headerSearch.toLowerCase();
      return (
        (r.leaveType && r.leaveType.toLowerCase().includes(q)) ||
        (r.reason && r.reason.toLowerCase().includes(q)) ||
        (r.status && r.status.toLowerCase().includes(q))
      );
    })
    : leaveRequests;
  const pagination = useListPagination(filteredLeaveRequests, headerSearch);

  const getStatusColor = (status) => {
    switch (String(status || '').toLowerCase()) {
      case 'approved': return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected': return 'bg-red-100 text-red-800 border-red-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const calculateDays = () => {
    if (scheduledDays !== null) return scheduledDays;
    if (!formData.startDate) return 0;
    if (formData.isHalfDay) return 0.5;
    if (!formData.endDate) return 0;
    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);
    const diffTime = Math.max(0, end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  if (loading && leaveRequests.length === 0) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-32 bg-gray-200 rounded-xl mb-6"></div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 h-96 bg-gray-200 rounded-xl"></div>
            <div className="h-96 bg-gray-200 rounded-xl"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-orange-600 via-orange-700 to-red-600 rounded-xl p-6 text-white relative overflow-hidden"
      >
        {/* <div className="absolute inset-0 bg-black opacity-10"></div> */}
        <div className="relative z-10 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold mb-2">
              Leave Management
            </h1>
            <p className="text-orange-100 mb-4">
              Submit and manage your leave requests with ease.
            </p>
          </div>
          <div className="flex items-center justify-end">
            {/* <div className="text-sm text-orange-200">
              Available Leave Balance: {leaveBalance.totalLeaveDays || 0} days
            </div> */}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowForm(!showForm)}
              icon={showForm ? XMarkIcon : PlusIcon}
            >
              {showForm ? 'Close Form' : 'New Request'}
            </Button>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Leave Request Form */}
        <div className="lg:col-span-2">
          <AnimatePresence>
            {showForm && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-6"
              >
                <Card>
                  <Card.Header>
                    <h3 className="text-lg font-semibold">Submit Leave Request</h3>
                    <p className="text-sm text-gray-600">Fill in the details for your leave application</p>
                  </Card.Header>
                  <Card.Body>
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Remove leave type selection - system auto-determines paid/unpaid */}
                        <div className="md:col-span-2">
                          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="flex items-start space-x-3">
                              <div className="flex-shrink-0">
                                <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <div>
                                <h4 className="text-sm font-medium text-blue-800">Automatic Leave Type Assignment</h4>
                                <p className="text-sm text-blue-700 mt-1">
                                  The system will automatically assign your leave as <strong>Paid Leave</strong> if you have available balance,
                                  otherwise it will be marked as <strong>Unpaid Leave</strong>.
                                </p>
                                <p className="text-xs text-blue-600 mt-2">
                                  Current Paid Leave Balance: <strong>{leaveBalance.l_balance || 0} days</strong>
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center space-x-4">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              name="isHalfDay"
                              checked={formData.isHalfDay}
                              onChange={handleChange}
                              className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                            />
                            <span className="ml-2 text-sm text-gray-700">Half Day</span>
                          </label>

                          {formData.isHalfDay && (
                            <div className="flex items-center space-x-2">
                              <span className="text-sm text-gray-600">Period:</span>
                              <select
                                name="halfDayPeriod"
                                value={formData.halfDayPeriod}
                                onChange={handleChange}
                                className="px-3 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                              >
                                <option value="morning">Morning</option>
                                <option value="afternoon">Afternoon</option>
                              </select>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Start Date *
                          </label>
                          <input
                            type="date"
                            name="startDate"
                            value={formData.startDate}
                            onChange={handleChange}
                            min={minimumLeaveDate}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            End Date {!formData.isHalfDay && '*'}
                          </label>
                          <input
                            type="date"
                            name="endDate"
                            value={formData.isHalfDay ? formData.startDate : formData.endDate}
                            onChange={handleChange}
                            min={formData.startDate || minimumLeaveDate}
                            disabled={formData.isHalfDay}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-100 disabled:text-gray-500"
                            required={!formData.isHalfDay}
                          />
                          {formData.isHalfDay && (
                            <p className="text-xs text-gray-500 mt-1">
                              End date is automatically set to start date for half-day leaves
                            </p>
                          )}
                        </div>
                      </div>

                      {scheduleError && <p role="alert" className="text-sm text-red-700">{scheduleError}</p>}
                      {scheduledDays === 0 && <p className="text-sm text-blue-700">These dates are weekly offs or holidays; no leave is needed.</p>}
                      {scheduledDays !== null && calculateDays() > 0 && (
                        <div className="p-4 bg-blue-50 rounded-lg">
                          <p className="text-sm text-blue-800">
                            <strong>Duration:</strong> {calculateDays()} day{calculateDays() !== 1 ? 's' : ''}
                            {formData.isHalfDay && (
                              <span className="ml-2 text-blue-600">
                                ({formData.halfDayPeriod} session)
                              </span>
                            )}
                          </p>
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Reason *
                        </label>
                        <textarea
                          name="reason"
                          value={formData.reason}
                          onChange={handleChange}
                          rows={4}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          placeholder="Please provide a reason for your leave request..."
                          required
                        />
                      </div>

                      <div className="flex space-x-4">
                        <Button type="submit" loading={loading}>
                          Submit Request
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setShowForm(false)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </form>
                  </Card.Body>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Leave Requests History */}
          <Card className="bg-white border-gray-200">
            <Card.Header className="bg-white border-gray-200">
              <h3 className="text-lg font-semibold">My Leave Requests</h3>
              <p className="text-sm text-gray-600">Track your submitted leave applications</p>
            </Card.Header>
            <Card.Body>
              <div ref={pagination.topRef}><ListPagination {...pagination} compact /></div>
              <div className="space-y-4">
                {(() => {
                  return filteredLeaveRequests.length > 0 ? (
                    pagination.items.map((request, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className={`p-4 border-2 rounded-lg ${getStatusColor(request.status)}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <span
                                className={`px-2 py-1 text-xs font-medium rounded-full text-white ${request.isPaid ? 'bg-green-600' : 'bg-red-600'
                                  }`}
                              >
                                {request.leaveType || (request.isPaid ? 'Paid Leave' : 'Unpaid Leave')}
                              </span>
                              <span className="text-sm text-gray-600">
                                {request.days || request.duration || calculateDays()} day{(request.days || request.duration || calculateDays()) !== 1 ? 's' : ''}
                              </span>
                              {request.isFullDay === false && (
                                <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                                  Half Day ({request.halfDayPeriod || 'N/A'})
                                </span>
                              )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="text-gray-600">From:</span>
                                <span className="ml-2 font-medium">
                                  {formatDateDisplay(request.fromDate || request.startDate)}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-600">To:</span>
                                <span className="ml-2 font-medium">
                                  {formatDateDisplay(request.toDate || request.endDate || request.fromDate || request.startDate)}
                                </span>
                              </div>
                            </div>

                            <p className="text-sm text-gray-700 mt-2">{request.reason}</p>

                            {request.approvalReason && request.status === 'approved' && (
                              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded">
                                <p className="text-sm font-medium text-green-700">Approval Note:</p>
                                <p className="text-sm text-green-600">{request.approvalReason}</p>
                              </div>
                            )}

                            {request.rejectionReason && request.status === 'rejected' && (
                              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
                                <p className="text-sm font-medium text-red-700">Rejection Reason:</p>
                                <p className="text-sm text-red-600">{request.rejectionReason}</p>
                              </div>
                            )}

                            {request.approverComments && !request.approvalReason && !request.rejectionReason && (
                              <div className="mt-3 p-3 bg-white bg-opacity-50 rounded">
                                <p className="text-sm font-medium text-gray-700">Manager Comments:</p>
                                <p className="text-sm text-gray-600">{request.approverComments}</p>
                              </div>
                            )}
                          </div>

                          <div className="flex items-center space-x-2 ml-4">
                            {getStatusIcon(request.status)}
                            <span className="text-sm font-medium capitalize">{request.status}</span>
                          </div>
                        </div>
                        {request.status === 'approved' && getCancellationStatus(request.id) === 'pending' && (
                          <p className="mt-4 border-t border-amber-200 pt-3 text-sm font-medium text-amber-700">
                            Cancellation request pending
                          </p>
                        )}
                        {request.status === 'approved' && !getCancellationStatus(request.id) && (
                          <div className="mt-4 border-t border-gray-200 pt-3">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setCancellingLeave(request);
                                setCancellationReason('');
                              }}
                            >
                              Cancel approved leave
                            </Button>
                          </div>
                        )}
                      </motion.div>
                    ))
                  ) : (
                    <div className="text-center py-12">
                      <DocumentTextIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No Leave Requests</h3>
                      <p className="text-gray-600 mb-4">
                        {headerSearch ? 'No leave requests match your search.' : "You haven't submitted any leave requests yet."}
                      </p>
                      {!headerSearch && !showForm && (
                        <Button onClick={() => setShowForm(true)} icon={PlusIcon}>
                          Submit Your First Request
                        </Button>
                      )}
                    </div>
                  );
                })()}
              </div>
              <ListPagination {...pagination} label="leave requests" disabled={loading} />
            </Card.Body>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Leave Taken Count */}
          <Card>
            <Card.Header>
              <h3 className="text-lg font-semibold">Leave Taken</h3>
            </Card.Header>
            <Card.Body>
              <div className="space-y-4">
                {leaveTypes.slice(0, 6).map((type) => {
                  // Calculate approved leave count for this leave type
                  const approvedLeaves = leaveRequests.filter(request =>
                    request.status === 'approved' &&
                    (request.leaveTypeCode === type.code ||
                      (type.code === 'PL' && request.leaveType === 'Paid Leave') ||
                      (type.code === 'UL' && request.leaveType === 'Unpaid Leave'))
                  );

                  const totalDaysTaken = approvedLeaves.reduce((sum, request) =>
                    sum + (request.days || request.duration || 1), 0
                  );

                  return (
                    <div key={type.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: type.color }}
                        ></div>
                        <div>
                          <span className="text-sm font-medium text-gray-900">{type.name}</span>
                          <p className="text-xs text-gray-600">
                            {type.annualLimit} days/year • {type.isPaid ? 'Paid' : 'Unpaid'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="font-medium text-gray-900">
                          {totalDaysTaken}
                        </span>
                        <p className="text-xs text-gray-600">taken</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card.Body>
          </Card>

          {/* Quick Stats */}
          {/* <Card>
            <Card.Header>
              <h3 className="text-lg font-semibold">This Year</h3>
            </Card.Header>
            <Card.Body>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Total Taken</span>
                  <span className="font-medium text-orange-600">12 days</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Remaining</span>
                  <span className="font-medium text-green-600">18 days</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Pending Requests</span>
                  <span className="font-medium text-yellow-600">
                    {leaveRequests.filter(r => r.status === 'pending').length}
                  </span>
                </div>
              </div>
            </Card.Body>
          </Card> */}

          {/* Leave Calendar */}
          <Card>
            <Card.Header>
              <h3 className="text-lg font-semibold">Upcoming Holidays</h3>
              <p className="text-sm text-gray-600">Next 30 days</p>
            </Card.Header>
            <Card.Body>
              <div className="space-y-3">
                {upcomingHolidays.length > 0 ? (
                  upcomingHolidays.map((holiday) => (
                    <div key={holiday.id || `${holiday.date}-${holiday.name}`} className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{holiday.name}</p>
                        <p className="text-xs text-gray-600">
                          {formatDateDisplay(holiday.date)}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No upcoming holidays in the next 30 days
                  </p>
                )}
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>
      {cancellingLeave && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Request leave cancellation</h3>
            <p className="mt-2 text-sm text-gray-600">
              This paid leave will be restored only after HR approves the cancellation.
            </p>
            <textarea
              className="mt-4 w-full rounded-lg border border-gray-300 p-3"
              rows={4}
              value={cancellationReason}
              onChange={(event) => setCancellationReason(event.target.value)}
              placeholder="Why are you cancelling this leave?"
            />
            <div className="mt-5 flex gap-3">
              <Button onClick={submitCancellation} loading={submittingCancellation}>Send request</Button>
              <Button variant="outline" disabled={submittingCancellation} onClick={() => setCancellingLeave(null)}>Keep leave</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveRequest;
